// ── Settings search index for Command Palette (Cmd+K) ──
// Matched against title, description, and keywords via substring search.

export interface SettingsSearchEntry {
  id: string;
  title: string;
  description: string;
  route: string;
  keywords: string[];
  icon: string;
}

export const SETTINGS_SEARCH_INDEX: SettingsSearchEntry[] = [
  // ── Your Business ──
  {
    id: 'settings-profile', title: 'Business profile',
    description: 'Name, address, phone, logo, timezone',
    route: '/settings/profile', icon: '\uD83C\uDFE2',
    keywords: ['business name', 'address', 'phone', 'logo', 'timezone', 'profile', 'identity', 'brand'],
  },
  {
    id: 'settings-hours', title: 'Business hours',
    description: 'Weekly schedule, holidays, closed dates',
    route: '/settings/hours', icon: '\uD83D\uDD50',
    keywords: ['hours', 'schedule', 'open', 'close', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday', 'holiday', 'closed'],
  },
  {
    id: 'settings-team', title: 'Team & roles',
    description: 'Permissions, commission rules, operator config',
    route: '/settings/team', icon: '\uD83D\uDC65',
    keywords: ['team', 'roles', 'permissions', 'commission', 'operator', 'chair', 'split', 'percentage', 'booth', 'employee', 'contractor'],
  },

  // ── Client Experience ──
  {
    id: 'settings-booking', title: 'Booking page',
    description: 'Slug, branding, hero, reviews',
    route: '/settings/booking', icon: '\uD83C\uDF10',
    keywords: ['booking', 'page', 'slug', 'url', 'link', 'branding', 'hero', 'reviews', 'testimonials', 'kiosk', 'online booking', 'appointment'],
  },
  {
    id: 'settings-services', title: 'Services & pricing',
    description: 'Dynamic pricing, intro offers, service catalog',
    route: '/settings/services', icon: '\uD83C\uDFF7',
    keywords: ['services', 'pricing', 'price', 'dynamic', 'peak', 'off-peak', 'demand', 'intro', 'offer', 'discount', 'first visit', 'new client', 'rate', 'cost', 'fee', 'catalog'],
  },
  {
    id: 'settings-fields', title: 'Client fields',
    description: 'Custom intake fields per industry',
    route: '/settings/fields', icon: '\uD83D\uDCDD',
    keywords: ['client', 'fields', 'custom', 'intake', 'form'],
  },

  // ── Payments & Billing ──
  {
    id: 'settings-payments', title: 'Payment setup',
    description: 'Stripe Connect — accept card payments',
    route: '/settings/payments', icon: '\uD83D\uDCB3',
    keywords: ['stripe', 'connect', 'payment', 'card', 'credit', 'debit', 'accept payments', 'payout', 'bank', 'deposit', 'merchant'],
  },
  {
    id: 'settings-invoicing', title: 'Invoice branding',
    description: 'Logo, accent colour, terms, HST, footer',
    route: '/settings/invoicing', icon: '\uD83E\uDDFE',
    keywords: ['invoice', 'branding', 'logo', 'colour', 'color', 'accent', 'terms', 'payment terms', 'HST', 'tax', 'footer', 'quote', 'net 15', 'net 30', 'due on receipt'],
  },
  {
    id: 'settings-tax', title: 'Tax & compliance',
    description: 'HST rates, CRA categories, mileage rates',
    route: '/settings/tax', icon: '\uD83E\uDDEE',
    keywords: ['tax', 'HST', 'GST', 'CRA', 'compliance', 'mileage', 'deduction', 'write-off', 'expense categories', 'rate'],
  },
  {
    id: 'settings-subscription', title: 'Your subscription',
    description: 'Current plan, upgrade, billing history',
    route: '/settings/subscription', icon: '\uD83D\uDC51',
    keywords: ['subscription', 'plan', 'billing', 'upgrade', 'downgrade', 'free', 'pro', 'business', 'trial', 'cancel'],
  },

  // ── Communication ──
  {
    id: 'settings-notifications', title: 'Notifications',
    description: 'Email preferences, auto-replies, reminders',
    route: '/settings/notifications', icon: '\uD83D\uDD14',
    keywords: ['notifications', 'email', 'reminder', 'auto-reply', 'confirmation', 'booking confirmation', 'review request', 'alert', 'push'],
  },
  {
    id: 'settings-marketing', title: 'Marketing automation',
    description: 'Visit recency, birthday, no-show campaigns',
    route: '/settings/marketing', icon: '\uD83D\uDCE3',
    keywords: ['marketing', 'automation', 'campaign', 'email', 'birthday', 'no-show', 'follow-up', 'recency', 'win-back'],
  },
  {
    id: 'settings-language', title: 'Language & translation',
    description: 'App language, client language, auto-translate',
    route: '/settings/language', icon: '\uD83C\uDF10',
    keywords: ['language', 'translation', 'translate', 'french', 'english', 'russian', 'auto-translate', 'messaging'],
  },

  // ── Tools & Integrations ──
  {
    id: 'settings-ai', title: 'AI features',
    description: 'Receipt scanning, smart replies, summaries',
    route: '/settings/ai', icon: '\u2728',
    keywords: ['AI', 'artificial intelligence', 'receipt', 'scan', 'scanner', 'smart reply', 'suggestion', 'summary', 'daily summary'],
  },
  {
    id: 'settings-directory', title: 'Directory listing',
    description: 'Appear on CrewBit Explore',
    route: '/settings/directory', icon: '\uD83D\uDCCD',
    keywords: ['directory', 'listing', 'explore', 'public', 'visible', 'cover photo', 'marketplace'],
  },
  {
    id: 'settings-embed', title: 'Embed widget',
    description: 'Add booking to your website',
    route: '/settings/embed', icon: '\uD83D\uDCBB',
    keywords: ['embed', 'widget', 'iframe', 'website', 'integration', 'snippet', 'code', 'wordpress'],
  },
  {
    id: 'settings-data', title: 'Data & privacy',
    description: 'Export data, delete account, PIPEDA',
    route: '/settings/data', icon: '\uD83D\uDEE1',
    keywords: ['data', 'privacy', 'export', 'download', 'delete', 'account', 'PIPEDA'],
  },

  // ── Import / Export ──
  {
    id: 'settings-import', title: 'Import configuration',
    description: 'Upload a config file or use a starter pack',
    route: '/settings/import', icon: '\uD83D\uDCE5',
    keywords: ['import', 'config', 'configuration', 'starter pack', 'template', 'setup', 'onboarding'],
  },
  {
    id: 'settings-export', title: 'Export configuration',
    description: 'Download your business setup as JSON',
    route: '/settings/export', icon: '\uD83D\uDCE4',
    keywords: ['export', 'config', 'configuration', 'download', 'backup', 'JSON', 'clone'],
  },
];
